import { array, object, ref, string, type AnyObject, type ArraySchema } from 'yup';

import { getPasswordStrength } from '../../../../../utils/validation/password/password.util';
import { APP_ENABLE_ANONYMOUS_REPORT } from '../../../../../configs/app.config';
import { fileValidation } from '../../../../../utils/validation/formValidation.util';
import { isMultiPartner } from '../../../../../utils/partner/partner.util';

import type { TFunction } from '../../../../../types/i18n/i18n.type';
import type { TAvailableValuesType } from '../../../../../types/components/components.type';

export const SUPPORTED_PREFIXES = {
  polish: '+48',
  czechRepublic: '+420',
} as const;

export const SUPPORTED_PREFIXES_DEFAULT = SUPPORTED_PREFIXES.polish;

type TSupportedPhonePrefixesKey = keyof typeof SUPPORTED_PREFIXES;
export type TSupportedPrefixes = (typeof SUPPORTED_PREFIXES)[TSupportedPhonePrefixesKey];

export const availablePhonePrefixes: TAvailableValuesType<TSupportedPrefixes> = [
  {
    value: SUPPORTED_PREFIXES_DEFAULT,
    label: 'Poland +48',
  },
  {
    value: SUPPORTED_PREFIXES.czechRepublic,
    label: 'Czech Republic +420',
  },
];

export const availableRelationWithOrganization = (t: TFunction): TAvailableValuesType => [
  { value: 'fullTimeEmployee', label: t('labels.fullTimeEmployee') },
  { value: 'contractor', label: t('labels.contractor') },
  { value: 'businessPartner', label: t('labels.businessPartner') },
  { value: 'subcontractor', label: t('labels.subcontractor') },
  { value: 'volunteer', label: t('labels.volunteer') },
  { value: 'intern', label: t('labels.intern') },
  { value: 'other', label: t('labels.other') },
];

export const availablePersonalDataDisclosure = (t: TFunction): TAvailableValuesType => [
  {
    value: 'true',
    label: t('labels.yes'),
  },
  {
    value: 'false',
    label: t('labels.no'),
  },
];

const requiredMessage = 'generic.required';

export const REGULATION_VALUE = 'regulation';

export const requiredConsentsValidation = (
  required: string[],
): ArraySchema<unknown[] | undefined, AnyObject, unknown, ''> =>
  array().test('requiredConsents', requiredMessage, (value) => {
    if (!Array.isArray(value)) {
      return false;
    }

    return required.every((val) => value.includes(val));
  });

export const signallerFormSchema = object()
  .shape({
    reporter: APP_ENABLE_ANONYMOUS_REPORT ? string() : string().required(requiredMessage),
    partnerNames: isMultiPartner()
      ? array().of(string()).required(requiredMessage).min(1, requiredMessage)
      : array().of(string()),
    subject: array().of(string()).required(requiredMessage).min(1, requiredMessage),
    description: string().required(requiredMessage).min(10, requiredMessage),
    reportedPersson: string().required(requiredMessage).min(5, requiredMessage),
    witness: string(),
    evidence: string().required(requiredMessage).min(3, requiredMessage),
    evidenceFiles: fileValidation(
      'invalid.filesSize',
      'invalid.filesTotalSize',
      'invalid.filesExtension',
    ),
    email: string().lowercase(),
    phone: array()
      .length(2, requiredMessage)
      .of(string())
      .test(
        'phoneValidation',
        'invalid.phoneFormat',
        (value) => !!value && (value[1] === '' || /^\d{9}$/.test(value[1] || '')),
      ),
    personalDataDisclosure: array().of(string()).min(1, requiredMessage),
    password: string()
      .test('empty-or-validate', 'invalid.passwordTooShortOrTooLong', (value) => {
        if (value === '') {
          return true;
        }

        return !!value && value.length >= 10 && value.length <= 32;
      })
      .test('noWhiteSpace', 'invalid.passwordWhiteSpaces', (value) => {
        if (value === '') {
          return true;
        }

        return !(value?.startsWith(' ') || value?.endsWith(' ') || value?.includes(' '));
      })
      .test('passwordStrength', 'invalid.passwordNotBeMediumOrWeak', (value) => {
        if (value === '') {
          return true;
        }

        return !['weak', 'medium'].includes(getPasswordStrength(value || ''));
      }),
    confirmPassword: string().oneOf([ref('password')], 'invalid.confirmPassword'),
    regulation: requiredConsentsValidation([REGULATION_VALUE]),
    relationWithOrganization: array().of(string()),
    street: string(),
    zip: string().test('empty-or-validate', 'invalid.zip', (value) => {
      if (value === '') {
        return true;
      }

      return !!value && /^\d{2}-?\d{3}$/.test(value);
    }),
    city: string(),
    country: string(),
  })
  .required();

export const FORM_EXCEPTIONS = {
  noRecaptcha: 'No recpatcha',
};
